import React from 'react';
import Style from './Governance.module.css';
import {
  ACTION_META, CATEGORY_LABEL, CATEGORY_ORDER,
  SITE_ACTION_OPTIONS, SITE_RESPONSE_ACTION_OPTIONS, isHiddenSiteRule,
} from './GovernanceShared';

// ─────────────────────────────────────────────────────────────────────────────
// SiteDataControls — the per-site data-control rule table (replaces the old
// DlpOverrideForm). Rows come from `props.rules` — the site draft's copied rule
// set when editing, or the published DATA policy's rules when creating — NOT the
// scan-rule master. This makes the table mirror the Data Controls tab exactly:
// Custom rules are included automatically, and disabled/deleted DATA rules never
// appear. The two system-managed domain-check rules are hidden.
//
// WEB: one action column per rule (the copied rules carry ChannelCode ANY).
// AI:  the copied rules expand into a PROMPT row + a RESPONSE row per DATA rule;
//      they render as Prompt + Response columns (Response restricted to
//      Ignore/Report). Before a site is created (create-mode preview) the DATA
//      rules are single-channel, so a single action column is shown.
//
// Editable (action select + Enabled toggle) only when the caller passes
// editable=true (disposition = SANCTIONED AND version = DRAFT); otherwise every
// cell renders as a read-only badge. Edits are delegated upward via
// onRuleChange(ruleId, patch) — the editor stages them locally and flushes on Save.
// ─────────────────────────────────────────────────────────────────────────────

const ActionBadge = ({ code }) => {
  const a = ACTION_META[code] || { label: code, cls: 'neutral' };
  return <span className={`${Style.badge} ${Style[a.cls]}`}>{a.label}</span>;
};

const EnabledBadge = ({ isActive }) =>
  isActive
    ? <span className={`${Style.badge} ${Style.low}`}>Enabled</span>
    : <span className={`${Style.badge} ${Style.neutral}`}>Disabled</span>;

const SourceBadge = ({ sourceCode }) =>
  sourceCode === 'DEFAULT'
    ? <span className={`${Style.badge} ${Style.neutral}`}>Default</span>
    : <span className={`${Style.badge} ${Style.primaryBadge}`}>Custom</span>;

const ActionSelect = ({ value, options, onChange, disabled }) => (
  <select
    className={Style.dlpSelect}
    data-v={value}
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
  >
    {options.map((a) => <option key={a} value={a}>{(ACTION_META[a] || {}).label || a}</option>)}
  </select>
);

// Collapse the flat rule rows into logical rules. For AI each DATA rule is two
// rows (PROMPT + RESPONSE) that must render as one line; WEB/DATA rows are a single
// ANY channel. Keying on the full copied identity keeps two distinct rules that
// happen to share a scan rule (e.g. two Custom rules) separate.
const groupLogical = (rows) => {
  const groups = [];
  const byKey = new Map();
  for (const r of rows) {
    const key = [r.scanRuleId, r.sourceCode, r.ruleDescription, r.categoryCode].join('|');
    let g = byKey.get(key);
    if (!g) {
      g = { key, sample: r, prompt: null, response: null, any: null };
      byKey.set(key, g);
      groups.push(g);
    }
    const ch = r.channelCode || 'ANY';
    if (ch === 'PROMPT') g.prompt = r;
    else if (ch === 'RESPONSE') g.response = r;
    else g.any = r;
  }
  return groups;
};

const SiteDataControls = ({ controlType, rules, editable, onRuleChange }) => {
  const isAi = controlType === 'AI';
  const visible = (Array.isArray(rules) ? rules : []).filter((r) => !isHiddenSiteRule(r));

  const sensitive = groupLogical(visible.filter((r) => r.ruleFamilyCode === 'SENSITIVE_DATA'));
  const fileTransfer = groupLogical(visible.filter((r) => r.ruleFamilyCode === 'FILE_TRANSFER'));

  // Group sensitive-data logical rules by category (mirrors DataControls ordering).
  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, items: sensitive.filter((g) => (g.sample.categoryCode || 'CUSTOM') === cat) }))
    .filter((g) => g.items.length > 0);

  // Clamp a source action to the AI RESPONSE-allowed set (WARN/BLOCK -> REPORT),
  // mirroring the server-side copy (PRC_Governance_CopyDataRulesToSite). Used to
  // preview the Response value for a rule that has not been channel-split yet (the
  // create-mode preview shows the single-channel ANY DATA rules that will be
  // copied into a PROMPT + RESPONSE pair once the site is created).
  const clampResponse = (a) => (a === 'WARN' || a === 'BLOCK' ? 'REPORT' : (a || 'REPORT'));

  const groupActive = (g) => {
    const rep = g.prompt || g.any || g.response;
    return !!(rep && rep.isActive);
  };

  const toggleEnabled = (g) => {
    if (!onRuleChange) return;
    const next = !groupActive(g);
    [g.prompt, g.response, g.any].forEach((row) => { if (row) onRuleChange(row.ruleId, { isActive: next }); });
  };

  const changeAction = (row, value) => {
    if (onRuleChange && row) onRuleChange(row.ruleId, { actionCode: value });
  };

  // Action cell(s) for a logical rule. The cell count MUST match the header:
  // WEB renders one "Action" cell; AI always renders a Prompt cell AND a Response
  // cell (even before a rule is channel-split — the create-mode preview shows the
  // single-channel ANY DATA rules that will be copied into PROMPT + RESPONSE).
  const actionCells = (g) => {
    if (!isAi) {
      const row = g.any || g.prompt || g.response || g.sample;
      return (
        <td>
          {editable
            ? <ActionSelect value={row.actionCode || 'REPORT'} options={SITE_ACTION_OPTIONS} onChange={(v) => changeAction(row, v)} />
            : <ActionBadge code={row.actionCode} />}
        </td>
      );
    }
    // AI — always two cells so the body lines up with the Prompt/Response header.
    const promptRow = g.prompt || g.any || g.sample;
    const promptVal = (promptRow && promptRow.actionCode) || 'REPORT';
    return (
      <>
        <td>
          {editable && promptRow
            ? <ActionSelect value={promptVal} options={SITE_ACTION_OPTIONS} onChange={(v) => changeAction(promptRow, v)} />
            : <ActionBadge code={promptVal} />}
        </td>
        <td>
          {g.response
            ? (editable
                ? <ActionSelect value={g.response.actionCode || 'REPORT'} options={SITE_RESPONSE_ACTION_OPTIONS} onChange={(v) => changeAction(g.response, v)} />
                : <ActionBadge code={g.response.actionCode} />)
            /* No RESPONSE row yet (create-mode preview): show the value the server
               will copy — the clamped source action — read-only. */
            : <ActionBadge code={clampResponse(promptVal)} />}
        </td>
      </>
    );
  };

  const enabledCell = (g) => (
    <td>
      {editable
        ? <input type="checkbox" checked={groupActive(g)} onChange={() => toggleEnabled(g)} style={{ cursor: 'pointer' }} />
        : <EnabledBadge isActive={groupActive(g)} />}
    </td>
  );

  // Column count for the empty-state row / header (Category|Rule|Source|Action(s)|Enabled).
  // AI always renders 2 action cells (Prompt + Response) in both header and body; WEB renders 1.
  const actionColCount = isAi ? 2 : 1;
  const sensitiveCols = 3 + actionColCount + 1;
  const fileCols = 2 + actionColCount + 1;

  const channelHeaders = isAi
    ? (<><th>Prompt</th><th>Response</th></>)
    : (<th>Action</th>);

  return (
    <div style={{ width: '100%' }}>
      {/* ── Sensitive Data Detection ── */}
      <div className={Style.card}>
        <div className={Style.cardHeader}>
          <div className={Style.cardTitle}>Sensitive Data Detection</div>
          <div className={Style.cardSubtitle}>
            {isAi
              ? 'Copied from Data Controls. Prompt = data sent to the app; Response = data returned. Response is limited to Ignore / Report.'
              : 'Copied from Data Controls. Adjust the action or disable a rule for this site.'}
          </div>
        </div>
        <div className={Style.cardBody}>
          <table className={Style.table}>
            <thead>
              <tr>
                <th>Category</th><th>Detection Rule</th><th>Source</th>
                {channelHeaders}
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr><td colSpan={sensitiveCols} style={{ textAlign: 'center', padding: '24px' }}>No detection rules</td></tr>
              )}
              {grouped.map((cg) =>
                cg.items.map((g, idx) => (
                  <tr key={g.key}>
                    {idx === 0 && (
                      <td className={Style.categoryCell} rowSpan={cg.items.length}>
                        {CATEGORY_LABEL[cg.cat] || cg.cat}
                      </td>
                    )}
                    <td>{g.sample.ruleDescription}</td>
                    <td><SourceBadge sourceCode={g.sample.sourceCode} /></td>
                    {actionCells(g)}
                    {enabledCell(g)}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── File Transfer Controls ── */}
      <div className={Style.card}>
        <div className={Style.cardHeader}>
          <div className={Style.cardTitle}>File Transfer Controls</div>
          <div className={Style.cardSubtitle}>Upload, download, and filename restrictions copied from Data Controls.</div>
        </div>
        <div className={Style.cardBody}>
          <table className={Style.table}>
            <thead>
              <tr>
                <th>Rule Name</th><th>Source</th>
                {channelHeaders}
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {fileTransfer.length === 0 && (
                <tr><td colSpan={fileCols} style={{ textAlign: 'center', padding: '24px' }}>No file transfer rules</td></tr>
              )}
              {fileTransfer.map((g) => (
                <tr key={g.key}>
                  <td><strong>{g.sample.ruleDescription}</strong></td>
                  <td><SourceBadge sourceCode={g.sample.sourceCode} /></td>
                  {actionCells(g)}
                  {enabledCell(g)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SiteDataControls;
